/**
 * Alerts store — capped FIFO of event-driven alerts.
 * See spec §23.
 */
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { EventEnvelope, EventSeverity } from '@/lib/mqtt/schemas/events';

const ALERT_CAP = 200;

export interface AlertItem extends EventEnvelope {
  ack_by?: string;
  ack_time?: string;
}

interface AlertsStore {
  items: AlertItem[];
  push: (e: EventEnvelope) => void;
  acknowledge: (eventId: string, user: string) => void;
  /** Remove from feed without resolving (mockup dismiss action). */
  dismiss: (eventId: string) => void;
  clear: () => void;
}

export const useAlertsStore = create<AlertsStore>()(
  devtools(
    (set) => ({
      items: [],
      push: (e) =>
        set((s) => {
          // dedupe by event_id
          const filtered = s.items.filter((i) => i.event_id !== e.event_id);
          const next = [{ ...e }, ...filtered].slice(0, ALERT_CAP);
          return { items: next };
        }),
      acknowledge: (eventId, user) =>
        set((s) => ({
          items: s.items.map((i) =>
            i.event_id === eventId
              ? { ...i, acknowledged: true, ack_by: user, ack_time: new Date().toISOString() }
              : i,
          ),
        })),
      dismiss: (eventId) =>
        set((s) => ({ items: s.items.filter((i) => i.event_id !== eventId) })),
      clear: () => set({ items: [] }),
    }),
    { name: 'alerts' },
  ),
);

export const SEVERITY_ORDER: Record<EventSeverity, number> = {
  crit: 3,
  warn: 2,
  info: 1,
  ok: 0,
};
